import React from "react";
import { FiArrowUpRight } from "react-icons/fi";
import {
  HiOutlineMail,
  HiOutlineBell,
  HiOutlineChatAlt2,
  HiOutlineClipboardList,
} from "react-icons/hi";
import { FaQuestionCircle } from "react-icons/fa";
import { CheckCircleIcon } from "@heroicons/react/solid";
import { Tooltip } from "react-tooltip";
import SelectOption from "components/common/Select/Select";
import SwitchWithIcon from "components/common/SwitchWithIcon";
import Loader from "components/common/Loader/Loader";
import { classNames, parseBooleanVal } from "Utils/utils";
import { PAYROLL_PLATFORMS } from "../../../../store/constants";
import refreshIcon from "../../../../assets/svg/refresh.svg";

const SettingsCard = ({
  title,
  description,
  children,
  className = "",
  compact = false,
  fillHeight = false,
}) => (
  <div
    className={classNames(
      "flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm",
      compact ? "p-5" : "p-6",
      fillHeight && "h-full",
      className
    )}
  >
    <div className={classNames("flex-shrink-0", compact ? "mb-4" : "mb-5")}>
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      {description && (
        <p className="mt-1 text-sm text-gray-500 leading-relaxed">{description}</p>
      )}
    </div>
    {fillHeight ? (
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    ) : (
      children
    )}
  </div>
);

const ToggleRow = ({
  icon: Icon,
  label,
  helper,
  checked,
  onChange,
  ariaLabel,
  disabled = false,
}) => (
  <div className="flex items-stretch gap-3 py-3 first:pt-0 last:pb-0 border-b border-gray-100 last:border-b-0">
    <div className="flex-shrink-0 mt-0.5 text-gray-400">
      <Icon className="h-5 w-5" aria-hidden="true" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-800">{label}</p>
      {helper && (
        <p className="mt-1 text-xs text-gray-500 leading-relaxed">{helper}</p>
      )}
    </div>
    <div className="flex-shrink-0 flex items-center">
      <SwitchWithIcon
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        aria-label={ariaLabel || label}
      />
    </div>
  </div>
);

const OrganizationPreferencesSection = ({
  serviceTypes,
  serviceTypesLoading = false,
  participantType,
  setParticipantType,
  register,
  showEmailNotificationToggle,
  emailNotification,
  setEmailNotification,
  showDocumentCommentsToggle,
  allowDocumentComments,
  setAllowDocumentComments,
  showUserJourneyTrackingToggle,
  userJourneyTracking,
  setUserJourneyTracking,
  userJourneyTrackingDisabled = false,
  selectedCareSector,
  onCareSectorChange,
  selectedShiftScreenFundingTypes,
  setSelectedShiftScreenFundingTypes,
  careSectorOptions,
  shiftScreenFundingTypeOptions,
  normalizeShiftFundingTypeOptions,
  getMandatoryShiftFundingTypes,
  isCheckPayrollAuthLoading,
  isAuthenticateForPayrolls,
  payrollAccountTypes,
  payrollAccount,
  setPayrollAccount,
  xeroSyncLoader,
  syncXeroLedgerAccounts,
  onConnectXero,
  invoiceEmailValue,
  invoiceEmailError,
  inputErrorClass,
  showInvoiceStartNumber = false,
  invoiceStartNumberError,
  invoiceStartNumberChanged = false,
  originalInvoiceStartNumber = null,
  setInvoiceStartNumberChanged,
  xeroUseRosterInvoiceNumber = false,
  setXeroUseRosterInvoiceNumber,
}) => {
  const isInvoiceEmailValid =
    invoiceEmailValue &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invoiceEmailValue.trim());

  const mandatoryLabels = getMandatoryShiftFundingTypes(selectedCareSector?.value ?? "ndis")
    .map((value) => shiftScreenFundingTypeOptions.find((option) => option.value === value)?.label)
    .filter(Boolean);

  const showNotificationsCard =
    showEmailNotificationToggle ||
    showDocumentCommentsToggle ||
    showUserJourneyTrackingToggle;

  return (
    <div className="mt-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">
          Organization &amp; Service Preferences
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Manage display defaults, notifications, and billing integrations.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:items-stretch">
        <div className="order-2 flex h-full flex-col gap-4 lg:order-1">
          {showNotificationsCard && (
            <SettingsCard
              compact
              title="Notifications & Permissions"
              description="Control email alerts and document upload permissions for your organisation."
            >
              {showEmailNotificationToggle && (
                <ToggleRow
                  icon={HiOutlineBell}
                  label="Send email notifications to SP Admin account"
                  helper="Receive alerts when important account events occur."
                  checked={emailNotification}
                  onChange={setEmailNotification}
                  ariaLabel="Toggle email notifications for SP Admin"
                />
              )}
              {showDocumentCommentsToggle && (
                <ToggleRow
                  icon={HiOutlineChatAlt2}
                  label="Allow comment option in Required Documents"
                  helper="Let staff add comments when uploading required documents."
                  checked={allowDocumentComments}
                  onChange={setAllowDocumentComments}
                  ariaLabel="Toggle comment option in required documents"
                />
              )}
              {showUserJourneyTrackingToggle && (
                <ToggleRow
                  icon={HiOutlineClipboardList}
                  label="Enable Audit Log & User Journey Tracking"
                  helper="Record user activity and show Audit Log and User Sessions in the sidebar. Changes apply when you save."
                  checked={userJourneyTracking}
                  onChange={setUserJourneyTracking}
                  disabled={userJourneyTrackingDisabled}
                  ariaLabel="Toggle audit log and user journey tracking"
                />
              )}
            </SettingsCard>
          )}

          <SettingsCard
            compact
            fillHeight
            className="flex-1"
            title="Integrations & Billing"
            description="Connect accounting tools and configure invoice delivery settings."
          >
            <div className="flex flex-1 flex-col gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  XERO Ledger Account
                </label>
                {isCheckPayrollAuthLoading ? (
                  <Loader cssClass="text-black" isSmallView />
                ) : isAuthenticateForPayrolls !== null &&
                  parseBooleanVal(isAuthenticateForPayrolls[PAYROLL_PLATFORMS.XERO]) ? (
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <SelectOption
                        isMulti={false}
                        isSearch
                        name="xero_selected_account_code"
                        data={payrollAccountTypes.map((item) => ({
                          value: item.Code,
                          label: item.Name,
                        }))}
                        selectedOption={payrollAccount}
                        handleChange={setPayrollAccount}
                      />
                    </div>
                    <button
                      type="button"
                      title="Sync XERO Ledger Accounts"
                      aria-label="Sync XERO ledger accounts"
                      onClick={() => !xeroSyncLoader && syncXeroLedgerAccounts()}
                      disabled={xeroSyncLoader}
                      className={classNames(
                        "flex-shrink-0 rounded-lg border border-gray-200 bg-gray-50 p-2.5 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
                        xeroSyncLoader && "opacity-60 cursor-not-allowed"
                      )}
                    >
                      {xeroSyncLoader ? (
                        <Loader isSmallView cssClass="text-black mr-0 p-0 flex" />
                      ) : (
                        <img className="h-4 w-4" src={refreshIcon} alt="" />
                      )}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={onConnectXero}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-blue-100 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                  >
                    Connect Xero
                    <FiArrowUpRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
                <p className="mt-1.5 text-xs text-gray-500">
                  Link your Xero account to sync ledger accounts for invoicing.
                </p>
              </div>

              <div>
                <label
                  htmlFor="invoice_email"
                  className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1.5"
                >
                  <HiOutlineMail className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  Email for Invoices
                </label>
                <div className="relative">
                  <input
                    type="email"
                    name="invoice_email"
                    id="invoice_email"
                    placeholder="e.g. billing@company.com"
                    aria-invalid={!!invoiceEmailError}
                    aria-describedby={invoiceEmailError ? "invoice_email_error" : undefined}
                    className={classNames(
                      "did-floating-input pr-10",
                      invoiceEmailError && inputErrorClass,
                      isInvoiceEmailValid && "border-green-500 ring-1 ring-green-500"
                    )}
                    {...register("invoice_email", {
                      required: false,
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Please enter a valid email address",
                      },
                    })}
                  />
                  {isInvoiceEmailValid && (
                    <CheckCircleIcon
                      className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-green-500"
                      aria-hidden="true"
                    />
                  )}
                </div>
                {invoiceEmailError ? (
                  <span id="invoice_email_error" className="mt-1 block text-sm text-red-500">
                    {invoiceEmailError}
                  </span>
                ) : (
                  <p className="mt-1.5 text-xs text-gray-500">
                    Invoices and billing notifications will be sent to this address.
                  </p>
                )}
              </div>

              {showInvoiceStartNumber && (
                <div>
                  <div className="mb-1.5 flex items-center justify-between gap-3">
                    <label
                      htmlFor="invoice_start_number"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Rostering Invoice Start Number
                    </label>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      <span className="text-xs font-medium text-gray-600">Use on Xero</span>
                      <SwitchWithIcon
                        checked={xeroUseRosterInvoiceNumber}
                        onChange={setXeroUseRosterInvoiceNumber}
                        aria-label="Use rostering invoice number on Xero"
                      />
                    </div>
                  </div>
                  <input
                    type="text"
                    name="invoice_start_number"
                    id="invoice_start_number"
                    placeholder="e.g. 11105 or 01105"
                    className="did-floating-input"
                    maxLength={5}
                    autoComplete="off"
                    {...register("invoice_start_number", {
                      required: "Rostering Invoice Start Number is required",
                      pattern: {
                        value: /^[0-9]{1,5}$/,
                        message: "Please enter a valid number (1-5 digits)",
                      },
                      validate: (value) => {
                        if (value === "" || value === null || value === undefined) {
                          return "Rostering Invoice Start Number is required";
                        }
                        if (!/^[0-9]+$/.test(value)) {
                          return "Please enter a valid number";
                        }
                        if (value.length > 5) {
                          return "Invoice number cannot exceed 5 digits";
                        }
                        return true;
                      },
                      onChange: (e) => {
                        let sanitizedValue = e.target.value.replace(/[^0-9]/g, "");
                        if (sanitizedValue.length > 5) {
                          sanitizedValue = sanitizedValue.slice(0, 5);
                        }
                        e.target.value = sanitizedValue;

                        const currentValue = sanitizedValue === "" ? null : sanitizedValue;
                        const originalValue =
                          originalInvoiceStartNumber === null ||
                          originalInvoiceStartNumber === undefined ||
                          originalInvoiceStartNumber === ""
                            ? null
                            : String(originalInvoiceStartNumber);
                        setInvoiceStartNumberChanged(currentValue !== originalValue);
                      },
                    })}
                  />
                  {invoiceStartNumberError && (
                    <span className="mt-1 block text-sm text-red-500">
                      {invoiceStartNumberError}
                    </span>
                  )}
                  {invoiceStartNumberChanged && (
                    <div className="mt-2 rounded-md border border-yellow-200 bg-yellow-50 p-3">
                      <p className="text-sm text-yellow-800">
                        <strong>Warning:</strong> Changing this value will only affect invoices
                        created after this update. Existing invoices will remain unchanged.
                      </p>
                    </div>
                  )}
                  <p className="mt-1.5 text-xs text-gray-500">
                    Sets the starting number for future invoices. Maximum 5 digits allowed.
                  </p>
                </div>
              )}
            </div>
          </SettingsCard>
        </div>

        <SettingsCard
          fillHeight
          title="Default Views & Display"
          description="Configure how service types and care sector settings appear across the platform."
          className="order-1 lg:order-2"
        >
        <div className="flex flex-1 flex-col gap-4 min-h-0">
          <div>
            <div className="hidden">
              <select
                {...register("participant_type")}
                name="participant_type"
                className="hidden did-floating-input"
              />
            </div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Default Service Support View
            </label>
            <SelectOption
              isMulti
              isSearch={false}
              loading={serviceTypesLoading}
              data={serviceTypes.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
              selectedOption={participantType}
              handleChange={setParticipantType}
            />
            <p className="mt-1.5 text-xs text-gray-500">
              Select which service types appear by default in participant views.
            </p>
          </div>

          <div className="flex flex-1 flex-col rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3 min-h-0">
            <div>
              <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                Care Sector
                <button
                  type="button"
                  data-tooltip-id="care-sector-section-tooltip"
                  className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label="More information about care sector"
                >
                  <FaQuestionCircle className="h-3.5 w-3.5" />
                </button>
              </h4>
              <Tooltip
                id="care-sector-section-tooltip"
                place="top"
                className="z-[501] max-w-xs"
                content='The care sector is the industry focused on supporting vulnerable people, covering aged care for the elderly and NDIS for those with disabilities.'
              />
              <p className="mt-1 text-sm text-gray-600 leading-relaxed">
                Set the default care sector for your organisation. All participants are treated as this sector. Terminology and invoicing will follow this setting.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
                  Default care sector
                  <button
                    type="button"
                    data-tooltip-id="default-care-sector-tooltip"
                    className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label="More information about default care sector"
                  >
                    <FaQuestionCircle className="h-3.5 w-3.5" />
                  </button>
                </label>
                <Tooltip
                  id="default-care-sector-tooltip"
                  place="top"
                  className="z-[501] max-w-xs"
                  content='Changing NDIS to Aged-care will update "Participants" to "Consumers" and vice-versa.'
                />
                <SelectOption
                  isSearch={false}
                  data={careSectorOptions}
                  selectedOption={selectedCareSector}
                  handleChange={onCareSectorChange}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Line items visible on shift screen
                </label>
                <SelectOption
                  isMulti
                  isSearch={false}
                  data={shiftScreenFundingTypeOptions}
                  selectedOption={selectedShiftScreenFundingTypes}
                  handleChange={(options) =>
                    setSelectedShiftScreenFundingTypes(
                      normalizeShiftFundingTypeOptions(
                        options,
                        selectedCareSector?.value ?? "ndis"
                      )
                    )
                  }
                />
                <p className="mt-1.5 text-xs text-gray-500">
                  {mandatoryLabels.join(" and ")}{" "}
                  {mandatoryLabels.length > 1 ? "are" : "is"} always selected for your
                  system care sector.
                </p>
              </div>
            </div>
          </div>
        </div>
      </SettingsCard>
      </div>
    </div>
  );
};

export default OrganizationPreferencesSection;
